import { Suspense, useCallback } from 'react';
import { motion } from 'motion/react';
import { X, Maximize2, GripVertical, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { widgetJiggle } from './widgetAnimations';
import type { WidgetDef } from './widgetRegistry';

interface WidgetWrapperProps {
  def: WidgetDef;
  isEditMode: boolean;
  isMobile: boolean;
  width: number;
  height: number;
  onRemove: (id: string) => void;
  onExpand?: (id: string) => void;
  onOpenAddDrawer?: () => void;
}

function WidgetShimmer() {
  return (
    <div className="animate-pulse space-y-3 p-4">
      <div className="h-4 w-1/3 rounded bg-white/20" />
      <div className="h-8 w-2/3 rounded bg-white/20" />
      <div className="h-4 w-1/2 rounded bg-white/20" />
    </div>
  );
}

export function WidgetWrapper({
  def,
  isEditMode,
  isMobile,
  width,
  height,
  onRemove,
  onExpand,
  onOpenAddDrawer,
}: WidgetWrapperProps) {
  const { t } = useTranslation();
  const Component = def.component;

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onRemove(def.id);
    },
    [def.id, onRemove],
  );

  const handleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onExpand?.(def.id);
    },
    [def.id, onExpand],
  );

  return (
    <motion.div
      className={`group relative flex flex-col h-full w-full overflow-hidden rounded-[32px]
        backdrop-blur-2xl border shadow-xl
        transition-shadow duration-500
        ${isEditMode
          ? 'border-blue-400/30 ring-2 ring-blue-400/50 shadow-2xl'
          : 'hover:shadow-2xl'
        }`}
      style={{
        backgroundColor: 'var(--page-card-bg, rgba(255,255,255,0.3))',
        borderColor: isEditMode ? undefined : 'var(--page-card-border, rgba(255,255,255,0.4))',
        boxShadow: isEditMode
          ? undefined
          : '0 4px 20px rgba(0, 0, 0, 0.06), inset 0 0 0 1px var(--page-card-border, rgba(255,255,255,0.3))',
      }}
      variants={widgetJiggle}
      animate={isEditMode ? 'jiggle' : 'idle'}
    >
      {/* Header — title always visible, controls on hover/edit */}
      <div className="widget-drag-handle flex shrink-0 items-center justify-between px-3 pt-2 pb-1 cursor-grab active:cursor-grabbing">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`transition-opacity duration-300 ${isEditMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} style={{ color: 'var(--user-page-text)', opacity: 0.5 }}>
            <GripVertical size={16} />
          </div>
          <h3 className="text-eyebrow truncate">
            {t(def.labelKey)}
          </h3>
        </div>

        <div className={`flex gap-1 shrink-0 transition-opacity duration-300 ${isEditMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
          {onExpand && (
            <button
              onClick={handleExpand}
              className="p-1.5 hover:bg-white/40 rounded-full transition-colors"
              style={{ color: 'var(--user-page-text)', opacity: 0.5 }}
              title={t('home.widgets.expandWidget')}
            >
              <Maximize2 size={14} />
            </button>
          )}
          {onOpenAddDrawer && (
            <button
              onClick={(e) => { e.stopPropagation(); onOpenAddDrawer(); }}
              className="p-1.5 hover:bg-primary-500/20 rounded-full transition-colors text-primary-500"
              title={t('home.widgets.addWidget')}
            >
              <Plus size={14} />
            </button>
          )}
          <button
            onClick={handleRemove}
            className="p-1.5 bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white rounded-full transition-all"
            title={t('home.widgets.removeWidget')}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Content area — fills remaining space */}
      <div className="flex-1 min-h-0 px-3 pb-3 overflow-hidden">
        <div className={`h-full w-full transition-all duration-500 ${
          isEditMode ? 'blur-sm grayscale-[0.2] scale-95 opacity-50' : ''
        }`}>
          <Suspense fallback={<WidgetShimmer />}>
            <Component width={width} height={height} isEditMode={isEditMode} />
          </Suspense>
        </div>
      </div>

      {/* Edit overlay shield — prevents interaction while dragging */}
      {isEditMode && (
        <div className="absolute inset-0 z-10 cursor-grab active:cursor-grabbing" />
      )}
    </motion.div>
  );
}
